/**
 * Drummond Club landing page — lightweight interactions.
 * No external carousel library. Uses native scroll-snap + a small
 * controller for arrows, dots, and disabled states.
 */
(function () {
  'use strict';

  function initCarousel(root) {
    var track = root.querySelector('[data-dc-track]');
    var prevBtn = root.querySelector('[data-dc-prev]');
    var nextBtn = root.querySelector('[data-dc-next]');
    var dotsWrap = root.querySelector('[data-dc-dots]');
    var originalItems = Array.prototype.slice.call(root.querySelectorAll('[data-dc-item]'));

    if (!track || originalItems.length === 0) return;

    var realCount = originalItems.length;
    var loop = realCount > 1;

    // Clone the first/last slides onto the opposite ends so there's
    // always something to scroll into, then silently snap back to the
    // real slide once the clone has scrolled into view (see settleLoop).
    if (loop) {
      var firstClone = originalItems[0].cloneNode(true);
      var lastClone = originalItems[realCount - 1].cloneNode(true);
      track.insertBefore(lastClone, originalItems[0]);
      track.appendChild(firstClone);
    }

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-dc-item]'));

    // Dots reflect only the REAL slides, not the clones
    var dots = originalItems.map(function (_, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'DC-carousel__dot';
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dot.addEventListener('click', function () {
        goToDisplayIndex(loop ? i + 1 : i, true);
      });
      dotsWrap.appendChild(dot);
      return dot;
    });

    function itemStep() {
      if (items.length < 2) return items[0].offsetWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }

    function currentDisplayIndex() {
      var step = itemStep();
      if (!step) return 0;
      return Math.round(track.scrollLeft / step);
    }

    function targetLeftFor(i) {
      var clamped = Math.max(0, Math.min(items.length - 1, i));
      return items[clamped].offsetLeft - track.offsetLeft;
    }

    function goToDisplayIndex(i, smooth) {
      track.scrollTo({ left: targetLeftFor(i), behavior: smooth ? 'smooth' : 'auto' });
    }

    // Instant reposition (bypasses CSS scroll-behavior: smooth) — used
    // to snap from a clone back to the matching real slide unnoticed.
    function jumpToDisplayIndex(i) {
      track.scrollLeft = targetLeftFor(i);
    }

    function realIndexFromDisplay(d) {
      if (!loop) return d;
      return ((d - 1) % realCount + realCount) % realCount;
    }

    function updateDots() {
      var d = currentDisplayIndex();
      var realIdx = realIndexFromDisplay(d);
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === realIdx);
      });
      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === d);
      });
    }

    function settleLoop() {
      if (!loop) return;
      var d = currentDisplayIndex();
      if (d <= 0) {
        jumpToDisplayIndex(realCount);
      } else if (d >= items.length - 1) {
        jumpToDisplayIndex(1);
      }
    }

    function updateUI() {
      settleLoop();
      updateDots();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goToDisplayIndex(currentDisplayIndex() - 1, true);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goToDisplayIndex(currentDisplayIndex() + 1, true);
      });
    }

    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateUI, 120);
    }, { passive: true });

    window.addEventListener('resize', function () {
      jumpToDisplayIndex(currentDisplayIndex());
      updateDots();
    });

    if (loop) jumpToDisplayIndex(1);
    updateDots();
  }

  /**
   * Partner finder: client-side text + state filtering over the
   * pre-rendered result cards. This does NOT plot map pins itself —
   * pin rendering is expected to come from the theme's existing
   * data-js-map component (the same one used on partner detail pages),
   * which reads data-markers/data-settings on the .DC-finder__map node.
   * If that component needs the marker list rebuilt after filtering,
   * hook into the 'dc:finder:filtered' custom event dispatched below.
   */
  function initFinder(root) {
    var searchInput = root.querySelector('[data-dc-finder-search]');
    var stateSelect = root.querySelector('[data-dc-finder-state]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-dc-finder-item]'));

    if (!items.length) return;

    function applyFilters() {
      var term = (searchInput && searchInput.value || '').trim().toLowerCase();
      var state = (stateSelect && stateSelect.value || 'All');
      var visible = [];

      items.forEach(function (item) {
        var name = (item.getAttribute('data-name') || '').toLowerCase();
        var itemState = item.getAttribute('data-state') || '';
        var matchesTerm = !term || name.indexOf(term) !== -1;
        var matchesState = state === 'All' || itemState === state;
        var show = matchesTerm && matchesState;
        item.classList.toggle('is-hidden', !show);
        if (show) visible.push(item);
      });

      root.dispatchEvent(new CustomEvent('dc:finder:filtered', {
        bubbles: true,
        detail: {
          visible: visible.map(function (item) {
            return {
              name: item.getAttribute('data-name'),
              lat: parseFloat(item.getAttribute('data-lat')),
              lng: parseFloat(item.getAttribute('data-lng'))
            };
          })
        }
      }));
    }

    if (searchInput) searchInput.addEventListener('input', applyFilters);
    if (stateSelect) stateSelect.addEventListener('change', applyFilters);
  }

  /**
   * Your Member Benefits carousel — deliberately kept as its own
   * standalone function (not reusing initCarousel) so a problem here
   * can never affect the offer carousels above. Simpler than the
   * offers carousel: no infinite-loop cloning, arrows just clamp at
   * the first/last card.
   */
  function initBenefitsCarousel(root) {
    var track = root.querySelector('[data-dc-benefits-track]');
    var prevBtn = root.querySelector('[data-dc-benefits-prev]');
    var nextBtn = root.querySelector('[data-dc-benefits-next]');
    var dotsWrap = root.querySelector('[data-dc-benefits-dots]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-dc-benefits-item]'));

    if (!track || items.length === 0) return;

    var dots = items.map(function (_, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'DC-carousel__dot';
      dot.setAttribute('aria-label', 'Go to benefit ' + (i + 1));
      dot.addEventListener('click', function () {
        scrollToIndex(i);
      });
      dotsWrap.appendChild(dot);
      return dot;
    });

    function itemStep() {
      if (items.length < 2) return items[0].offsetWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }

    function currentIndex() {
      var step = itemStep();
      if (!step) return 0;
      return Math.round(track.scrollLeft / step);
    }

    function scrollToIndex(i) {
      var clamped = Math.max(0, Math.min(items.length - 1, i));
      track.scrollTo({ left: items[clamped].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }

    function updateUI() {
      var idx = currentIndex();
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === idx);
      });
      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === idx);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToIndex(currentIndex() - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToIndex(currentIndex() + 1);
      });
    }

    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateUI, 60);
    }, { passive: true });

    window.addEventListener('resize', updateUI);
    updateUI();
  }

  function initAll() {
    document.querySelectorAll('[data-dc-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-dc-benefits]').forEach(initBenefitsCarousel);
    document.querySelectorAll('.DC-finder').forEach(initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init if a carousel section is added/edited live in the theme editor
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      evt.target.querySelectorAll('[data-dc-carousel]').forEach(initCarousel);
      evt.target.querySelectorAll('[data-dc-benefits]').forEach(initBenefitsCarousel);
    });
  }
})();